import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
  Campaign, SaveCampaign, CampaignWorkflow, CampaignMemberLocation, CampaignProgress, CampaignLeadPlanItem, CampaignLeadPlansUpdated, CampaignMembersChange, CampaignMembersChanged,
} from '../models/campaign.models';
import type { WorkItem } from '../pages/leads/board/leads-board.component';
import { DataQuery } from '../models/data-source';
import { PagedDto } from '../models/paged.dto';

@Injectable({ providedIn: 'root' })
export class CampaignApiService {
  constructor(private readonly http: HttpClient) {}

  list(query: DataQuery) {
    let params = new HttpParams().set('page', query.page).set('pageSize', query.pageSize)
      .set('search', query.search).set('desc', query.desc ?? false);
    if (query.sort) params = params.set('sort', query.sort);
    for (const key of ['status', 'serviceLine']) {
      const value = query.filters[key];
      if (value != null && value !== '') params = params.set(key, String(value));
    }
    return this.http.get<PagedDto<Campaign>>('/api/campaigns/page', { params });
  }

  updateLeadPlans(id: number, items: CampaignLeadPlanItem[]) {
    return this.http.put<CampaignLeadPlansUpdated>(`/api/campaigns/${id}/lead-plans`, { items });
  }
  progress(id: number) { return this.http.get<CampaignProgress>(`/api/campaigns/${id}/progress`); }
  workflow(id: number) { return this.http.get<CampaignWorkflow>(`/api/campaigns/${id}/workflow`); }
  memberLocations(id: number) { return this.http.get<CampaignMemberLocation[]>(`/api/campaigns/${id}/members/locations`); }
  changeMembers(id: number, change: CampaignMembersChange) {
    return this.http.put<CampaignMembersChanged>(`/api/campaigns/${id}/members`, change);
  }
  transition(id: number, to: 'Completed' | 'Cancelled') { return this.http.post<Campaign>(`/api/campaigns/${id}/transition`, { to }); }
  targets(id: number, query: DataQuery) {
    const params = new HttpParams().set('page', query.page).set('pageSize', query.pageSize)
      .set('search', query.search).set('sort', query.sort ?? 'title').set('desc', query.desc ?? false);
    return this.http.get<PagedDto<WorkItem>>(`/api/campaigns/${id}/targets`, { params });
  }

  delete(id: number) { return this.http.delete<void>(`/api/campaigns/${id}`); }
  get(id: number) { return this.http.get<Campaign>(`/api/campaigns/${id}`); }
  create(request: SaveCampaign) { return this.http.post<Campaign>('/api/campaigns', request); }
  update(id: number, request: SaveCampaign) { return this.http.put<Campaign>(`/api/campaigns/${id}`, request); }
}

export function campaignErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 404) return 'This campaign is no longer available.';
    if (error.status === 403) return 'You do not have permission to access this campaign.';
    if (typeof error.error?.error === 'string') return error.error.error;
    if (typeof error.error?.message === 'string') return error.error.message;
  }
  return fallback;
}
